import { useEffect, useMemo, useState } from "react";
import { Alert, LinearProgress, MenuItem, Stack, TextField } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { DataField } from "@/components/DataField";
import { DataTable, type Column } from "@/components/DataTable";
import { FilterModal } from "@/components/FilterModal";
import { LargeDialog } from "@/components/LargeDialog";
import usePagination from "@/hooks/pagination";
import { useFilterState } from "@/hooks/useFilterState";
import { useAuth } from "@/context/AuthContext";
import { useAutoresDaEscala } from "@/hooks/useAutoresDaEscala";
import { rotuloDoTipo } from "@/components/escala/tiposDia";
import {
  ANO_MAXIMO_COMPETENCIA,
  ANO_MINIMO_COMPETENCIA,
  autorDaAlteracao,
  formatDataBR,
  formatDataHoraBR,
  inicioDoDiaISO,
  listarLogGerada,
  MOTIVOS_LOG,
  rotuloDoMotivoLog,
  type ListarLogParams,
  type LogEntrada,
} from "@/api/escala.service";

interface EscalaLogModalProps {
  geradaId: string;
  // Só o log deste colaborador (botão da linha do espelho).
  colaborador?: { id: string; nome: string };
  // Escala com carga horária: mostra a carga de antes e a de depois.
  comCarga?: boolean;
  onClose: () => void;
}

const MSG_DATA_FORA = "Data fora do intervalo";
const MSG_PERIODO_INVERTIDO = "A data final não pode ser anterior à inicial";

// "AAAA-MM-DD" vazio ou com ano dentro do intervalo das competências. O teto é um a menos porque
// o "até" soma um dia, e o dia seguinte a 31/12 do último ano já não é um instante válido.
function dataDeFiltroValida(data: string) {
  if (!data) return true;
  const ano = Number(data.slice(0, 4));
  return ano >= ANO_MINIMO_COMPETENCIA && ano < ANO_MAXIMO_COMPETENCIA;
}

const FILTROS_VAZIOS = { colaborador: "", motivo: "", user_id: "", de: "", ate: "" };

const colunas = (autores: Map<string, string>, comColaborador: boolean, comCarga: boolean): Column<LogEntrada>[] => [
  { key: "created_at", label: "Data e hora", render: (l) => formatDataHoraBR(l.created_at) },
  { key: "user_id", label: "Autor", render: (l) => autorDaAlteracao(l, autores) },
  ...(comColaborador ? [{ key: "colaborador_nome", label: "Colaborador" }] : []),
  { key: "data", label: "Dia", render: (l) => formatDataBR(l.data) },
  { key: "alteracao", label: "Alteração", render: (l) => `${rotuloDoTipo(l.de)} → ${rotuloDoTipo(l.para)}` },
  ...(comCarga
    ? [
        { key: "detalhe_de", label: "Carga anterior", render: (l: LogEntrada) => l.detalhe_de || "—" },
        { key: "detalhe_para", label: "Carga nova", render: (l: LogEntrada) => l.detalhe_para || "—" },
      ]
    : []),
  { key: "motivo", label: "Motivo", render: (l) => rotuloDoMotivoLog(l.motivo) },
];

// Só o que foi preenchido vai ao servidor; "até" é o começo do dia seguinte, porque o limite lá é exclusivo.
function filtrosParaParams(f: typeof FILTROS_VAZIOS) {
  const params: Partial<ListarLogParams> = {};
  if (f.colaborador.trim()) params.colaborador = f.colaborador.trim();
  if (f.motivo) params.motivo = f.motivo;
  if (f.user_id) params.user_id = f.user_id;
  if (f.de) params.de = inicioDoDiaISO(f.de);
  if (f.ate) params.ate = inicioDoDiaISO(f.ate, 1);
  return params;
}

// Montado só enquanto aberto: a página e o tamanho voltam ao padrão a cada abertura.
export function EscalaLogModal({ geradaId, colaborador, comCarga = false, onClose }: EscalaLogModalProps) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { inputValues, setInputValues, filtros, applyFilters, handleClearFilters } = useFilterState(
    FILTROS_VAZIOS,
    () => setPage(0),
  );

  const [itens, setItens] = useState<LogEntrada[]>([]);
  const autores = useAutoresDaEscala(clientId);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  const opcoesAutor = useMemo(
    () =>
      [...autores]
        .map(([id, nome]) => ({ id, nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
    [autores],
  );
  const erroDe = dataDeFiltroValida(inputValues.de) ? undefined : MSG_DATA_FORA;
  const periodoInvertido =
    !erroDe && dataDeFiltroValida(inputValues.ate) && !!inputValues.de && !!inputValues.ate && inputValues.ate < inputValues.de;
  const erroAte = !dataDeFiltroValida(inputValues.ate) ? MSG_DATA_FORA : periodoInvertido ? MSG_PERIODO_INVERTIDO : undefined;
  const filtrosAtivos = Object.values(filtros).filter((v) => v.trim()).length;

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(false);
    listarLogGerada(
      {
        gerada_id: geradaId,
        ...filtrosParaParams(filtros),
        ...(colaborador && { colaborador_id: colaborador.id }),
        page: page + 1,
        page_size: rowsPerPage,
      },
      clientId,
    )
      .then((r) => {
        if (cancelado) return;
        setItens(r.items ?? []);
        setTotal(r.total);
      })
      .catch(() => {
        if (cancelado) return;
        // Sem isso a tabela da consulta anterior ficaria sob o alerta, parecendo o resultado do filtro.
        setItens([]);
        setTotal(0);
        setErro(true);
      })
      .finally(() => !cancelado && setCarregando(false));
    return () => {
      cancelado = true;
    };
  }, [geradaId, colaborador?.id, clientId, filtros, page, rowsPerPage, setTotal]);

  return (
    <LargeDialog
      open
      size="medium"
      title={colaborador ? `Histórico de alterações — ${colaborador.nome}` : "Histórico de alterações"}
      onClose={onClose}
    >
      <Stack sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Stack direction="row" sx={{ flexShrink: 0 }}>
          <FilterModal
            activeCount={filtrosAtivos}
            applyDisabled={!!erroDe || !!erroAte}
            onApply={applyFilters}
            onClear={handleClearFilters}
          >
            {!colaborador && (
              <TextField
                label="Colaborador"
                size="small"
                fullWidth
                value={inputValues.colaborador}
                onChange={(e) => setInputValues((v) => ({ ...v, colaborador: e.target.value }))}
              />
            )}
            <TextField
              select
              label="Motivo"
              size="small"
              fullWidth
              value={inputValues.motivo}
              onChange={(e) => setInputValues((v) => ({ ...v, motivo: e.target.value }))}
            >
              <MenuItem value="">Todos</MenuItem>
              {MOTIVOS_LOG.map((m) => (
                <MenuItem key={m.valor} value={m.valor}>
                  {m.rotulo}
                </MenuItem>
              ))}
            </TextField>
            <SelectEntidade
              label="Autor"
              options={opcoesAutor}
              value={inputValues.user_id}
              onChange={(id) => setInputValues((v) => ({ ...v, user_id: id ?? "" }))}
              getLabel={(o) => o.nome}
              size="small"
            />
            <DataField
              label="Alterado de"
              size="small"
              fullWidth
              error={!!erroDe}
              helperText={erroDe}
              value={inputValues.de}
              onChange={(data) => setInputValues((v) => ({ ...v, de: data }))}
            />
            <DataField
              label="Alterado até"
              size="small"
              fullWidth
              error={!!erroAte}
              helperText={erroAte}
              value={inputValues.ate}
              onChange={(data) => setInputValues((v) => ({ ...v, ate: data }))}
            />
          </FilterModal>
        </Stack>

        <Stack spacing={2}>
          {carregando && <LinearProgress aria-label="Carregando histórico" />}
          {erro && <Alert severity="error">Não foi possível carregar o histórico desta escala.</Alert>}
          {!erro && !carregando && total === 0 && (
            <Alert severity="info">
              {filtrosAtivos > 0
                ? "Nenhuma alteração encontrada com os filtros aplicados."
                : colaborador
                  ? "Nenhuma alteração registrada para este colaborador nesta escala."
                  : "Nenhuma alteração registrada nesta escala."}
            </Alert>
          )}
          {total > 0 && (
            <DataTable
              data={itens}
              columns={colunas(autores, !colaborador, comCarga)}
              total={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={(linhas) => {
                setRowsPerPage(linhas);
                setPage(0);
              }}
            />
          )}
        </Stack>
      </Stack>
    </LargeDialog>
  );
}
